import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { formatDateTime, formatNumber, formatRelative } from '../ui/format';
import { Badge, ErrorState, Loading, PageHeader, Panel } from '../ui/Page';
import { DataTable, type Column } from '../ui/DataTable';
import {
  approvePost,
  cancelPost,
  deletePost,
  getPost,
  listAccounts,
  listPosts,
  postTone,
  POST_STATUSES,
  type PostStatus,
  type SocialAccount,
  type SocialPost,
} from './api';
import { AccountsDialog } from './AccountsDialog';
import { PostEditor } from './PostEditor';

/**
 * The calendar (context.md §79–§81).
 *
 * A month at a glance is the view a social team lives in — what goes out when, and where the
 * gaps are. The list underneath answers the other question: what needs somebody's attention
 * right now, which is anything waiting for approval and anything that failed. Both come from
 * the same query; the list is the month filtered, not a second screen.
 */
export function SocialPage(): React.JSX.Element {
  const client = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [month, setMonth] = useState(() => startOfMonth(new Date()));
  const [editing, setEditing] = useState<{ post?: SocialPost; date?: string } | null>(null);
  const [accountsOpen, setAccountsOpen] = useState(false);
  const [filter, setFilter] = useState<PostStatus | 'ALL'>('ALL');

  const selectedId = params.get('post');

  const accounts = useQuery({ queryKey: ['social-accounts'], queryFn: listAccounts });

  const range = useMemo(() => monthRange(month), [month]);
  const posts = useQuery({
    queryKey: ['social-posts', range.from],
    queryFn: () => listPosts(range),
  });

  const refresh = (): void => {
    void client.invalidateQueries({ queryKey: ['social-posts'] });
    void client.invalidateQueries({ queryKey: ['social-post'] });
  };

  // Memoised: `?? []` is a new array each render, and the grouping below depends on it.
  const rows = useMemo(() => posts.data?.posts ?? [], [posts.data?.posts]);
  const byDay = useMemo(() => groupByDay(rows), [rows]);
  const listed = posts.data
    ? filter === 'ALL'
      ? rows
      : rows.filter((post) => post.status === filter)
    : undefined;
  const connected = (accounts.data?.accounts ?? []).filter((a) => a.status === 'CONNECTED');

  const select = (id: string | null): void => {
    const next = new URLSearchParams(params);
    if (id) next.set('post', id);
    else next.delete('post');
    setParams(next, { replace: true });
  };

  const columns: Column<SocialPost>[] = [
    {
      key: 'when',
      header: t('social.when'),
      text: (post) =>
        post.scheduledAt ? formatDateTime(post.scheduledAt) : t('social.unscheduled'),
      render: (post) => (
        <span className="muted small">
          {post.scheduledAt ? formatDateTime(post.scheduledAt) : t('social.unscheduled')}
        </span>
      ),
    },
    {
      key: 'account',
      header: t('social.account'),
      text: (post) => `${post.accountName} ${t(`social.network.${post.network}`)}`,
      render: (post) => (
        <span className="cell-stack">
          <span>{post.accountName}</span>
          <span className="muted small">{t(`social.network.${post.network}`)}</span>
        </span>
      ),
    },
    {
      key: 'body',
      header: t('social.post'),
      text: (post) =>
        post.mediaFileIds.length > 0
          ? `${post.body} ${post.mediaFileIds.length} ${t('social.images')}`
          : post.body,
      render: (post) => (
        <div className="social-excerpt">
          {post.body}
          {post.mediaFileIds.length > 0 && (
            <span className="muted small">{` · ${post.mediaFileIds.length} ${t('social.images')}`}</span>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      header: t('crm.field.status'),
      text: (post) =>
        [
          t(`social.status.${post.status}`),
          post.status === 'FAILED' ? post.failureReason : null,
          post.authorType === 'AI_AGENT' ? t('social.byAgent') : null,
        ]
          .filter(Boolean)
          .join(' '),
      render: (post) => (
        <span className="cell-stack">
          <Badge tone={postTone(post.status)}>{t(`social.status.${post.status}`)}</Badge>
          {post.status === 'FAILED' && post.failureReason && (
            <span className="muted small">{post.failureReason}</span>
          )}
          {post.authorType === 'AI_AGENT' && (
            <span className="muted small">{t('social.byAgent')}</span>
          )}
        </span>
      ),
    },
    /*
     * Off by default.
     *
     * A month of posts is read by date and by state; the network, the hour it actually went out
     * and how many tries it took are what somebody chasing a failure asks for afterwards, and the
     * chooser remembers the answer on the account.
     */
    {
      key: 'network',
      header: t('social.networkLabel'),
      hidden: true,
      text: (post) => t(`social.network.${post.network}`),
      render: (post) => t(`social.network.${post.network}`),
    },
    {
      key: 'publishedAt',
      header: t('social.publishedAt'),
      hidden: true,
      text: (post) => (post.publishedAt ? formatDateTime(post.publishedAt) : ''),
      render: (post) =>
        post.publishedAt ? formatDateTime(post.publishedAt) : <span className="muted">—</span>,
    },
    {
      key: 'attempts',
      header: t('social.attempts'),
      hidden: true,
      align: 'end',
      render: (post) => formatNumber(post.attempts),
    },
    {
      key: 'created',
      header: t('common.createdAt'),
      hidden: true,
      align: 'end',
      text: (post) => formatDateTime(post.createdAt),
      render: (post) => formatDateTime(post.createdAt),
    },
  ];

  return (
    <div className="view view--wide">
      <PageHeader
        title={t('social.title')}
        description={t('social.description')}
        actions={
          <div className="cell-inline">
            <button
              type="button"
              className="button button--quiet button--compact"
              onClick={() => setAccountsOpen(true)}
            >
              {t('social.accounts')}
              {connected.length > 0 && (
                <span className="muted small">{` · ${connected.length}`}</span>
              )}
            </button>
            <button
              type="button"
              className="button button--primary button--compact"
              disabled={connected.length === 0}
              onClick={() => setEditing({})}
            >
              {t('social.newPost')}
            </button>
          </div>
        }
      />

      {accounts.data && connected.length === 0 && (
        <p className="alert alert--info">
          {t('social.noAccounts')}{' '}
          <button type="button" className="link" onClick={() => setAccountsOpen(true)}>
            {t('social.connectFirst')}
          </button>
        </p>
      )}

      <Panel>
        <div className="toolbar">
          <span className="cell-inline">
            <button
              type="button"
              className="button button--quiet button--compact"
              onClick={() => setMonth(addMonths(month, -1))}
              aria-label={t('social.previousMonth')}
            >
              ‹
            </button>
            <strong className="social-month">{monthLabel(month)}</strong>
            <button
              type="button"
              className="button button--quiet button--compact"
              onClick={() => setMonth(addMonths(month, 1))}
              aria-label={t('social.nextMonth')}
            >
              ›
            </button>
            <button
              type="button"
              className="button button--quiet button--compact"
              onClick={() => setMonth(startOfMonth(new Date()))}
            >
              {t('calendar.today')}
            </button>
          </span>
        </div>

        {posts.isLoading ? (
          <Loading />
        ) : posts.error ? (
          <ErrorState error={posts.error} onRetry={() => void posts.refetch()} />
        ) : (
          <div className="calendar">
            {WEEKDAYS.map((day) => (
              <div key={day} className="calendar__weekday">
                {day}
              </div>
            ))}
            {daysOf(month).map((day) => {
              const key = dayKey(day);
              const items = byDay.get(key) ?? [];
              const outside = day.getMonth() !== month.getMonth();

              return (
                <div
                  key={key}
                  className={`calendar__cell${outside ? ' calendar__cell--outside' : ''}${
                    isToday(day) ? ' calendar__cell--today' : ''
                  } is-clickable`}
                  onClick={() => connected.length > 0 && setEditing({ date: isoDate(day) })}
                >
                  <span className="calendar__number">{day.getDate()}</span>
                  {items.slice(0, 3).map((post) => (
                    <button
                      key={post.id}
                      type="button"
                      className={`calendar__event calendar__event--${post.status}`}
                      title={post.body}
                      onClick={(event) => {
                        event.stopPropagation();
                        select(post.id);
                      }}
                    >
                      {post.scheduledAt ? `${timeOf(post.scheduledAt)} ` : ''}
                      {networkMark(post.network)} {post.body}
                    </button>
                  ))}
                  {items.length > 3 && (
                    <span className="calendar__more">
                      {t('calendar.more').replace('{n}', String(items.length - 3))}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Panel>

      <Panel title={t('social.thisMonth')}>
        <div className="toolbar">
          <span className="cell-inline">
            <button
              type="button"
              className={`chip${filter === 'ALL' ? ' chip--active' : ''}`}
              onClick={() => setFilter('ALL')}
            >
              {t('social.all')}
            </button>
            {POST_STATUSES.filter((s) => s !== 'PUBLISHING').map((status) => {
              const count = rows.filter((post) => post.status === status).length;
              if (count === 0 && filter !== status) return null;

              return (
                <button
                  key={status}
                  type="button"
                  className={`chip${filter === status ? ' chip--active' : ''}`}
                  onClick={() => setFilter(status)}
                >
                  {t(`social.status.${status}`)}
                  <span className="muted small">{` ${count}`}</span>
                </button>
              );
            })}
          </span>
        </div>

        <DataTable
          tableKey="social-posts"
          rows={listed}
          columns={columns}
          rowKey={(post) => post.id}
          isLoading={posts.isLoading}
          error={posts.error}
          onRetry={() => void posts.refetch()}
          onRowClick={(post) => select(post.id)}
          empty={{ title: t('social.none') }}
        />
      </Panel>

      {selectedId && (
        <PostDrawer
          id={selectedId}
          onClose={() => select(null)}
          onEdit={(post) => {
            select(null);
            setEditing({ post });
          }}
          onChanged={refresh}
        />
      )}

      {editing && (
        <PostEditor
          accounts={connected}
          post={editing.post}
          defaultDate={editing.date}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            refresh();
          }}
        />
      )}

      <AccountsDialog
        open={accountsOpen}
        accounts={accounts.data?.accounts ?? []}
        onClose={() => setAccountsOpen(false)}
        onChanged={() => {
          void client.invalidateQueries({ queryKey: ['social-accounts'] });
          refresh();
        }}
      />
    </div>
  );
}

/** One post: what it says, where it stands, and what can be done about it. */
function PostDrawer({
  id,
  onClose,
  onEdit,
  onChanged,
}: {
  id: string;
  onClose: () => void;
  onEdit: (post: SocialPost) => void;
  onChanged: () => void;
}): React.JSX.Element {
  const [error, setError] = useState<string | null>(null);

  const post = useQuery({ queryKey: ['social-post', id], queryFn: () => getPost(id) });

  const act = useMutation({
    mutationFn: async (action: 'approve' | 'cancel' | 'delete') => {
      if (action === 'approve') await approvePost(id);
      else if (action === 'cancel') await cancelPost(id);
      else await deletePost(id);
    },
    onSuccess: (_result, action) => {
      setError(null);
      onChanged();
      if (action === 'delete') onClose();
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const data = post.data;
  const editable = data && data.status !== 'PUBLISHED' && data.status !== 'PUBLISHING';
  const cancelable = data && ['PENDING_APPROVAL', 'SCHEDULED', 'DRAFT'].includes(data.status);

  return (
    <aside className="drawer" role="dialog" aria-label={t('social.post')}>
      <div className="drawer__head">
        <strong>{data ? data.accountName : t('social.post')}</strong>
        <button type="button" className="button button--quiet button--compact" onClick={onClose}>
          {t('common.close')}
        </button>
      </div>

      {post.isLoading || !data ? (
        <Loading />
      ) : (
        <div className="drawer__body">
          <div className="cell-inline">
            <Badge tone={postTone(data.status)}>{t(`social.status.${data.status}`)}</Badge>
            <span className="muted small">{t(`social.network.${data.network}`)}</span>
          </div>

          <p className="social-body">{data.body}</p>

          {data.linkUrl && (
            <a href={data.linkUrl} target="_blank" rel="noreferrer" className="small">
              {data.linkUrl}
            </a>
          )}

          <dl className="facts">
            <dt>{t('social.when')}</dt>
            <dd>{data.scheduledAt ? formatDateTime(data.scheduledAt) : t('social.unscheduled')}</dd>

            {data.publishedAt && (
              <>
                <dt>{t('social.publishedAt')}</dt>
                <dd>
                  {formatDateTime(data.publishedAt)}
                  {data.externalUrl && (
                    <>
                      {' · '}
                      <a href={data.externalUrl} target="_blank" rel="noreferrer">
                        {t('social.openOnNetwork')}
                      </a>
                    </>
                  )}
                </dd>
              </>
            )}

            {data.status === 'FAILED' && (
              <>
                <dt>{t('social.failure')}</dt>
                <dd>{data.failureReason ?? '—'}</dd>
                <dt>{t('social.attempts')}</dt>
                <dd>{data.attempts}</dd>
              </>
            )}

            {data.status === 'SCHEDULED' && data.attempts > 0 && data.nextAttemptAt && (
              <>
                <dt>{t('social.retrying')}</dt>
                <dd>
                  {formatRelative(data.nextAttemptAt)}
                  <span className="muted small">{` · ${data.failureReason ?? ''}`}</span>
                </dd>
              </>
            )}

            {data.mediaFileIds.length > 0 && (
              <>
                <dt>{t('social.images')}</dt>
                <dd>{data.mediaFileIds.length}</dd>
              </>
            )}
          </dl>

          {data.metrics && (
            <div className="social-metrics">
              {(['impressions', 'reach', 'likes', 'comments', 'shares', 'clicks'] as const).map(
                (key) =>
                  data.metrics![key] !== null && (
                    <div key={key} className="social-metric">
                      <strong>{formatNumber(data.metrics![key])}</strong>
                      <span className="muted small">{t(`social.metric.${key}`)}</span>
                    </div>
                  ),
              )}
              <span className="muted small social-metrics__at">
                {t('social.capturedAt')} {formatRelative(data.metrics.capturedAt)}
              </span>
            </div>
          )}

          {error && (
            <p className="alert small" role="alert">
              {error}
            </p>
          )}

          <div className="confirm__actions">
            {data.status === 'PENDING_APPROVAL' && (
              <button
                type="button"
                className="button button--primary button--compact"
                disabled={act.isPending}
                onClick={() => act.mutate('approve')}
              >
                {t('social.approve')}
              </button>
            )}
            {editable && (
              <button
                type="button"
                className="button button--compact"
                disabled={act.isPending}
                onClick={() => onEdit(data)}
              >
                {t('social.edit')}
              </button>
            )}
            {cancelable && (
              <button
                type="button"
                className="button button--quiet button--compact"
                disabled={act.isPending}
                onClick={() => act.mutate('cancel')}
              >
                {t('social.cancel')}
              </button>
            )}
            {data.status !== 'PUBLISHED' && (
              <button
                type="button"
                className="button button--quiet button--danger button--compact"
                disabled={act.isPending}
                onClick={() => act.mutate('delete')}
              >
                {t('social.delete')}
              </button>
            )}
          </div>
        </div>
      )}
    </aside>
  );
}

// ---------------------------------------------------------------------------

const WEEKDAYS = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];

export function networkMark(network: SocialAccount['network']): string {
  return network === 'INSTAGRAM'
    ? 'IG'
    : network === 'FACEBOOK'
      ? 'FB'
      : network === 'LINKEDIN'
        ? 'IN'
        : '·';
}

function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function addMonths(date: Date, months: number): Date {
  return new Date(date.getFullYear(), date.getMonth() + months, 1);
}

/** Six weeks from the Sunday before the first, so the grid never changes height. */
function daysOf(month: Date): Date[] {
  const first = startOfMonth(month);
  const start = new Date(first);
  start.setDate(first.getDate() - first.getDay());

  return Array.from({ length: 42 }, (_, index) => {
    const day = new Date(start);
    day.setDate(start.getDate() + index);
    return day;
  });
}

function monthRange(month: Date): { from: string; to: string } {
  const days = daysOf(month);
  const end = new Date(days.at(-1)!);
  end.setDate(end.getDate() + 1);

  return { from: days[0]!.toISOString(), to: end.toISOString() };
}

function monthLabel(month: Date): string {
  return new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric' }).format(month);
}

function dayKey(date: Date): string {
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

function isoDate(date: Date): string {
  const pad = (value: number): string => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function isToday(date: Date): boolean {
  return dayKey(date) === dayKey(new Date());
}

function timeOf(iso: string): string {
  return new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' }).format(
    new Date(iso),
  );
}

function groupByDay(posts: SocialPost[]): Map<string, SocialPost[]> {
  const grouped = new Map<string, SocialPost[]>();

  for (const post of posts) {
    const key = dayKey(new Date(post.scheduledAt ?? post.createdAt));
    grouped.set(key, [...(grouped.get(key) ?? []), post]);
  }

  return grouped;
}
